const SSO_TOKEN_KEY = 'sso_token'
const SSO_USER_KEY = 'sso_user'
const SSO_EXPIRE_KEY = 'sso_expire'

export interface SSOUser {
  id: string
  username: string
  email?: string
  phone?: string
  avatar?: string
  roles?: string[]
  permissions?: string[]
}

export interface SSOToken {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresIn: number
}

// /**
//  * 检查是否已完成单点登录
//  * @description 验证本地存储的token是否存在且未过期
//  * @returns {boolean} true表示已登录且有效，false表示未登录或已过期
//  * @example
//  * // 检查登录状态
//  * if (isSingleLogin()) {
//  *   console.log('用户已登录')
//  * } else {
//  *   console.log('请先登录')
//  * }
//  */
// // export function isSingleLogin(): boolean {
// //   const token = getSSOToken()
// //   const expireTime = getSSOExpireTime()

// //   if (!token || !expireTime) {
// //     return false
// //   }

// //   const now = Date.now()
// //   return now < expireTime
// // }

/**
 * 获取SSO Token
 * @description 从localStorage中获取存储的访问令牌
 * @returns {string | null} Token字符串，如果不存在则返回null
 * @example
 * const token = getSSOToken()
 * if (token) {
 *   // 使用token发起API请求
 * }
 */
export function getSSOToken(): string | null {
  return localStorage.getItem(SSO_TOKEN_KEY)
}

/**
 * 设置SSO Token
 * @description 将访问令牌存储到localStorage
 * @param {string} token - 访问令牌字符串
 * @example
 * setSSOToken('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...')
 */
export function setSSOToken(token: string): void {
  localStorage.setItem(SSO_TOKEN_KEY, token)
}

/**
 * 获取SSO用户信息
 * @description 从localStorage中获取存储的用户信息对象
 * @returns {SSOUser | null} 用户信息对象，如果不存在则返回null
 * @example
 * const user = getSSOUser()
 * if (user) {
 *   console.log(user.username, user.email)
 * }
 */
export function getSSOUser(): SSOUser | null {
  const userStr = localStorage.getItem(SSO_USER_KEY)
  if (!userStr) return null
  try {
    return JSON.parse(userStr)
  } catch {
    return null
  }
}

/**
 * 设置SSO用户信息
 * @description 将用户信息对象存储到localStorage
 * @param {SSOUser} user - 用户信息对象
 * @example
 * setSSOUser({
 *   id: '1',
 *   username: 'admin',
 *   email: 'admin@example.com',
 *   roles: ['admin', 'user'],
 *   permissions: ['read', 'write', 'delete']
 * })
 */
export function setSSOUser(user: SSOUser): void {
  localStorage.setItem(SSO_USER_KEY, JSON.stringify(user))
}

/**
 * 获取SSO过期时间
 * @description 从localStorage中获取token的过期时间戳
 * @returns {number | null} 过期时间戳（毫秒），如果不存在则返回null
 * @example
 * const expireTime = getSSOExpireTime()
 * if (expireTime) {
 *   const remainingTime = expireTime - Date.now()
 *   console.log(`Token剩余时间: ${remainingTime / 1000}秒`)
 * }
 */
export function getSSOExpireTime(): number | null {
  const expire = localStorage.getItem(SSO_EXPIRE_KEY)
  return expire ? parseInt(expire, 10) : null
}

/**
 * 设置SSO过期时间
 * @description 计算并存储token的过期时间戳
 * @param {number} expiresIn - 有效期（秒）
 * @example
 * // 设置2小时后过期
 * setSSOExpireTime(7200)
 */
export function setSSOExpireTime(expiresIn: number): void {
  const expireTime = Date.now() + expiresIn * 1000
  localStorage.setItem(SSO_EXPIRE_KEY, expireTime.toString())
}

/**
 * 清除SSO信息
 * @description 从localStorage中清除所有SSO相关数据（token、用户信息、过期时间）
 * @example
 * // 退出登录时调用
 * clearSSO()
 */
export function clearSSO(): void {
  localStorage.removeItem(SSO_TOKEN_KEY)
  localStorage.removeItem(SSO_USER_KEY)
  localStorage.removeItem(SSO_EXPIRE_KEY)
}

// /**
//  * 检查SSO权限
//  * @description 检查当前登录用户是否具有指定的权限
//  * @param {string} permission - 权限标识
//  * @returns {boolean} true表示具有该权限，false表示没有
//  * @example
//  * if (checkSSOPermission('delete_user')) {
//  *   // 执行删除用户操作
//  * }
//  */
// export function checkSSOPermission(permission: string): boolean {
//   if (!isSingleLogin()) return false
//   const user = getSSOUser()
//   if (!user) return false
//   return user.permissions?.includes(permission) ?? false
// }

// /**
//  * 检查SSO角色
//  * @description 检查当前登录用户是否具有指定的角色
//  * @param {string} role - 角色标识
//  * @returns {boolean} true表示具有该角色，false表示没有
//  * @example
//  * if (checkSSORole('admin')) {
//  *   // 显示管理员菜单
//  * }
//  */
// export function checkSSORole(role: string): boolean {
//   if (!isSingleLogin()) return false
//   const user = getSSOUser()
//   if (!user) return false
//   return user.roles?.includes(role) ?? false
// }
